import { Plus, Loader2, ArrowDownCircle, RotateCcw, Clock, X, Calendar } from "lucide-react";
import { Button } from "@/components/flowly/infra/Button/Button";
import { PricingCards } from "@/components/flowly/PricingCards/PricingCards";
import { TokenPacks } from "@/components/flowly/TokenPacks/TokenPacks";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { usePricing } from "@/pages/Pricing/usePricing";
import { PURCHASES_ENABLED } from "@/config/featureFlags";
import { PLANS, getPlanPriceNIS } from "@/config/plans";
import type { PlanId } from "@/config/plans";
import { CancelSubscriptionDialog } from "@/components/flowly/CancelSubscriptionDialog/CancelSubscriptionDialog";

/** Currency symbol used on all about-to-be-charged surfaces. PayPlus charges in ILS regardless of UI language. */
const ILS_SYMBOL = "₪";

interface PricingContentProps {
  /**
   * "page" → full Pricing page body (Add-Tokens dialog, all four plans).
   * "modal" → credit-wall upgrade modal: token packs inline first, then the
   * paid plans only (FREE hidden).
   */
  variant: "page" | "modal";
}

/**
 * Shared pricing body used by both the /pricing page and the credit-wall
 * upgrade modal. Owns usePricing() so plan/token selection, coupons and the
 * downgrade/reactivation/cancel confirmation dialogs behave identically in
 * both surfaces — single source of truth for the buy flow.
 */
export function PricingContent({ variant }: PricingContentProps) {
  const isModal = variant === "modal";
  const {
    showTokensDialog,
    setShowTokensDialog,
    isProcessing,
    t,
    language,
    currencySymbol,
    handleSelectPlan,
    handleAddTokens,
    handlePurchaseTokens,
    getTokenPriceDisplay,
    getTokenOriginalPriceDisplay,
    pendingDowngrade,
    handleConfirmDowngrade,
    handleCancelDowngrade,
    pendingReactivation,
    handleConfirmReactivation,
    handleCancelReactivation,
    currentPlanId,
    currentPeriodEnd,
    scheduledPlanId,
    scheduledBillingPeriod,
    handleCancelScheduledChange,
    showCancelScheduledDialog,
    confirmCancelScheduledChange,
    handleCloseCancelScheduledDialog,
    currentBillingPeriod,
    showCancelDialog,
    handleCancelComplete,
    handleCancelDialogClose,
    couponCode,
    setCouponCode,
    couponLoading,
    couponResult,
    handleApplyCoupon,
    handleClearCoupon,
    isDebugUser,
    handleDebugPurchase,
  } = usePricing();

  return (
    <>
      {/* Wind-down notice — new purchases/subscriptions are closed. */}
      {!PURCHASES_ENABLED && (
        <div className="max-w-xl mx-auto mb-8 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-center">
          <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">
            {t.pricing.purchasesClosedNotice}
          </p>
        </div>
      )}

      {/* Processing Overlay — page only. In the modal a click navigates straight
          to /checkout, so a full-screen processing spinner is redundant/jarring. */}
      {!isModal && isProcessing && (
        <div className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center">
          <div className="bg-card rounded-3xl p-10 max-w-sm w-full border border-border shadow-2xl text-center">
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
            <p className="text-lg font-bold text-foreground">
              {t.paymentResult.processing}
            </p>
          </div>
        </div>
      )}

      {isModal ? (
        /* ---- Credit-wall modal: Add-Tokens button on top, then paid plans ---- */
        <div className="space-y-6">
          {/* Token-pack toggle pinned above the subscription plans so it stays
              visible. Click reveals the packs inline (no nested dialog). */}
          <div className="text-center">
            <Button
              variant="secondary"
              className="px-6 py-3 text-base font-bold border-2 border-brand/50 hover:border-brand hover:bg-brand/10"
              onClick={() => setShowTokensDialog(!showTokensDialog)}
              disabled={isProcessing}
            >
              <Plus className="w-5 h-5" />
              {t.pricing.addTokensBtn}
            </Button>
            {showTokensDialog && (
              <div className="mt-4 max-w-md mx-auto">
                <p className="text-sm text-muted-foreground mb-4 text-center">
                  {t.pricing.addTokensDialogDesc}
                </p>
                <TokenPacks
                  onPurchase={handlePurchaseTokens}
                  disabled={isProcessing || !PURCHASES_ENABLED}
                  currencySymbol={currencySymbol}
                  getPriceDisplay={getTokenPriceDisplay}
                  getOriginalPriceDisplay={getTokenOriginalPriceDisplay}
                />
              </div>
            )}
          </div>

          <PricingCards
            mode="full"
            excludePlanIds={["free"]}
            onSelectPlan={handleSelectPlan}
            disabled={isProcessing || !PURCHASES_ENABLED}
            couponCode={couponCode}
            onCouponCodeChange={setCouponCode}
            couponLoading={couponLoading}
            couponResult={couponResult}
            onApplyCoupon={handleApplyCoupon}
            onClearCoupon={handleClearCoupon}
            scheduledPlanId={scheduledPlanId}
            scheduledBillingPeriod={scheduledBillingPeriod}
            onCancelScheduledChange={handleCancelScheduledChange}
          />
        </div>
      ) : (
        /* ---- Pricing page: Add-Tokens dialog + all plans ---- */
        <>
          {/* Tokens Purchase Dialog */}
          <Dialog open={showTokensDialog} onOpenChange={setShowTokensDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-center text-2xl font-bold">
                  {t.pricing.addTokensDialogTitle}
                </DialogTitle>
              </DialogHeader>
              <div className="py-4">
                <p className="text-center text-muted-foreground text-sm mb-6">
                  {t.pricing.addTokensDialogDesc}
                </p>
                <TokenPacks
                  onPurchase={handlePurchaseTokens}
                  disabled={isProcessing || !PURCHASES_ENABLED}
                  currencySymbol={currencySymbol}
                  getPriceDisplay={getTokenPriceDisplay}
                  getOriginalPriceDisplay={getTokenOriginalPriceDisplay}
                />
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Tokens Button — shown to everyone; logged-out users who pick a
              pack are routed to signup by handlePurchaseTokens. */}
          <Button
            variant="secondary"
            className="mb-8 px-6 py-3 text-base font-bold border-2 border-brand/50 hover:border-brand hover:bg-brand/10"
            onClick={handleAddTokens}
            disabled={isProcessing}
          >
            <Plus className="w-5 h-5" />
            {t.pricing.addTokensBtn}
          </Button>

          {/* Scheduled Change Notice */}
          {scheduledPlanId && currentPeriodEnd && (
            <div className="max-w-xl mx-auto mb-8 flex items-center gap-3 rounded-xl bg-amber-500/10 border border-amber-500/20 px-4 py-3 text-start">
              <Clock className="w-5 h-5 text-amber-500 shrink-0" />
              <span className="text-sm text-amber-500 font-medium flex-1">
                {t.pricing.scheduledChangeNotice
                  .replace("{plan}", scheduledPlanId.toUpperCase())
                  .replace("{period}", scheduledBillingPeriod === "annual" ? t.profile.billingAnnual : t.profile.billingMonthly)
                  .replace("{date}", new Date(currentPeriodEnd).toLocaleDateString(
                    language === "he" ? "he-IL" : "en-US",
                    { year: "numeric", month: "long", day: "numeric" },
                  ))}
              </span>
              <button
                onClick={handleCancelScheduledChange}
                disabled={isProcessing}
                className="text-amber-500 hover:text-amber-400 disabled:opacity-50 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Dev-only ₪1 debug purchase — rendered only for admins (profile.role === 'admin').
              Used to exercise the real production payment flow end-to-end without
              paying full prices. Server-enforced (returns 404 for non-admins). */}
          {isDebugUser && (
            <div className="max-w-xl mx-auto mb-8 flex items-center justify-between gap-3 rounded-xl border border-dashed border-fuchsia-500/40 bg-fuchsia-500/5 px-4 py-3">
              <span className="text-sm text-fuchsia-300 font-medium">
                Debug plan (₪1/month recurring) — visible only to you
              </span>
              <Button
                variant="accent"
                size="sm"
                rounded="lg"
                onClick={handleDebugPurchase}
                disabled={isProcessing}
              >
                Buy DEBUG ₪1
              </Button>
            </div>
          )}

          {/* Shared Pricing Cards */}
          <PricingCards
            mode="full"
            onSelectPlan={handleSelectPlan}
            disabled={isProcessing || !PURCHASES_ENABLED}
            couponCode={couponCode}
            onCouponCodeChange={setCouponCode}
            couponLoading={couponLoading}
            couponResult={couponResult}
            onApplyCoupon={handleApplyCoupon}
            onClearCoupon={handleClearCoupon}
            scheduledPlanId={scheduledPlanId}
            scheduledBillingPeriod={scheduledBillingPeriod}
            onCancelScheduledChange={handleCancelScheduledChange}
          />
        </>
      )}

      {/* Downgrade Confirmation Dialog (scheduled at period end — no charge) */}
      <Dialog open={!!pendingDowngrade} onOpenChange={(open) => !open && handleCancelDowngrade()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="text-center sm:text-center">
            <div className="mx-auto mb-3 w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center shadow-lg shadow-amber-500/10">
              <ArrowDownCircle className="w-7 h-7 text-amber-500" />
            </div>
            <DialogTitle className="text-2xl font-bold">
              {t.pricing.confirmDowngradeTitle}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {t.pricing.confirmDowngradeMessage
                .replace("{from}", (currentPlanId ?? "").toUpperCase())
                .replace("{to}", (pendingDowngrade?.planName ?? "").toUpperCase())
                .replace("{date}", currentPeriodEnd
                  ? new Date(currentPeriodEnd).toLocaleDateString(language === "he" ? "he-IL" : "en-US", { year: "numeric", month: "long", day: "numeric" })
                  : "")}
            </DialogDescription>
          </DialogHeader>

          {pendingDowngrade && (() => {
            const currentPlanName = PLANS.find((p) => p.id === currentPlanId)?.name ?? (currentPlanId ?? "").toUpperCase();
            const currentPriceNum = currentPlanId
              ? getPlanPriceNIS(currentPlanId as PlanId, (currentBillingPeriod ?? "monthly") as "monthly" | "annual")
              : 0;
            const currentPeriodLabel = (currentBillingPeriod ?? "monthly") === "annual" ? t.pricing.periodYear : t.pricing.periodMonth;
            const newPriceNum = getPlanPriceNIS(pendingDowngrade.planId as PlanId, pendingDowngrade.billingCycle);
            const newPeriodLabel = pendingDowngrade.billingCycle === "annual" ? t.pricing.periodYear : t.pricing.periodMonth;
            const formattedDate = currentPeriodEnd
              ? new Date(currentPeriodEnd).toLocaleDateString(language === "he" ? "he-IL" : "en-US", { year: "numeric", month: "long", day: "numeric" })
              : "";
            return (
              <div className="space-y-3">
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 flex flex-col gap-3 overflow-hidden">
                  <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 text-xs font-bold uppercase tracking-wide">
                    <Calendar className="w-3.5 h-3.5" />
                    {t.pricing.confirmDowngradeTimelineTitle}
                  </div>
                  {/* Today row — current plan continues, no charge */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-md shrink-0 bg-amber-500 text-white">
                        {t.pricing.confirmDowngradeTodayLabel}
                      </span>
                      <span className="text-sm text-foreground truncate">
                        {t.pricing.confirmDowngradeCurrentContinues.replace("{plan}", currentPlanName)}
                      </span>
                    </div>
                    <span className="text-sm font-bold whitespace-nowrap text-emerald-600 dark:text-emerald-400">
                      {t.pricing.confirmDowngradeNoCharge}
                    </span>
                  </div>
                  {/* Scheduled row — new plan kicks in at period end */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-md shrink-0 bg-muted text-muted-foreground">
                        {t.pricing.confirmDowngradeScheduledLabel}
                      </span>
                      <span className="text-sm text-foreground truncate">
                        {t.pricing.confirmDowngradeStartingPlan
                          .replace("{date}", formattedDate)
                          .replace("{plan}", pendingDowngrade.planName.toUpperCase())}
                      </span>
                    </div>
                    <span className="text-sm font-bold whitespace-nowrap text-muted-foreground">
                      {newPriceNum > 0
                        ? `${ILS_SYMBOL}${newPriceNum}/${newPeriodLabel}`
                        : t.pricing.confirmDowngradeFreePrice}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground text-center px-2">
                  {t.pricing.confirmDowngradeExplanation
                    .replace("{currentPlan}", currentPlanName)
                    .replace("{newPlan}", pendingDowngrade.planName.toUpperCase())
                    .replace("{date}", formattedDate)
                    .replace("{currentPrice}", `${ILS_SYMBOL}${currentPriceNum}/${currentPeriodLabel}`)
                    .replace("{newPrice}", newPriceNum > 0 ? `${ILS_SYMBOL}${newPriceNum}/${newPeriodLabel}` : t.pricing.confirmDowngradeFreePrice)}
                </p>
              </div>
            );
          })()}

          <div className="flex gap-3 mt-1">
            <Button
              variant="secondary"
              size="md"
              rounded="xl"
              onClick={handleCancelDowngrade}
              className="flex-1"
            >
              {t.pricing.confirmDowngradeNo}
            </Button>
            <Button
              variant="accent"
              size="md"
              rounded="xl"
              onClick={handleConfirmDowngrade}
              disabled={isProcessing}
              className="flex-1"
            >
              {t.pricing.confirmDowngradeYes}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reactivation Confirmation Dialog (no charge) */}
      <Dialog open={!!pendingReactivation} onOpenChange={(open) => !open && handleCancelReactivation()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="text-center sm:text-center">
            <div className="mx-auto mb-3 w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center shadow-lg shadow-emerald-500/10">
              <RotateCcw className="w-7 h-7 text-emerald-500" />
            </div>
            <DialogTitle className="text-2xl font-bold">
              {pendingReactivation?.isPlanChange
                ? t.pricing.reactivateChangeTitle
                    .replace("{plan}", pendingReactivation.planName)
                : t.pricing.reactivateTitle}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {pendingReactivation?.isPlanChange
                ? t.pricing.reactivateChangeDescription
                    .replace("{date}", currentPeriodEnd
                      ? new Date(currentPeriodEnd).toLocaleDateString(language === "he" ? "he-IL" : "en-US", { year: "numeric", month: "long", day: "numeric" })
                      : "")
                : t.pricing.reactivateDescription}
            </DialogDescription>
          </DialogHeader>

          <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-xl p-4">
            <p className="text-sm text-muted-foreground text-center">
              {pendingReactivation?.isPlanChange
                ? t.pricing.reactivateChangeDescription
                    .replace("{date}", currentPeriodEnd
                      ? new Date(currentPeriodEnd).toLocaleDateString(language === "he" ? "he-IL" : "en-US", { year: "numeric", month: "long", day: "numeric" })
                      : "")
                : t.pricing.reactivateDescription}
            </p>
          </div>

          <div className="flex gap-3 mt-1">
            <Button
              variant="secondary"
              size="md"
              rounded="xl"
              onClick={handleCancelReactivation}
              className="flex-1"
            >
              {t.pricing.reactivateNo}
            </Button>
            <Button
              variant="accent"
              size="md"
              rounded="xl"
              onClick={handleConfirmReactivation}
              disabled={isProcessing}
              className="flex-1"
            >
              {pendingReactivation?.isPlanChange
                ? t.pricing.reactivateChangeYes
                : t.pricing.reactivateYes}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Scheduled Change Confirmation Dialog */}
      <Dialog open={showCancelScheduledDialog} onOpenChange={(open) => !open && handleCloseCancelScheduledDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="text-center sm:text-center">
            <div className="mx-auto mb-3 w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center shadow-lg shadow-amber-500/10">
              <Clock className="w-7 h-7 text-amber-500" />
            </div>
            <DialogTitle className="text-2xl font-bold">
              {t.pricing.cancelScheduledTitle}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {t.pricing.cancelScheduledMessage
                .replace("{plan}", PLANS.find((p) => p.id === currentPlanId)?.name ?? currentPlanId?.toUpperCase() ?? "")
                .replace("{period}", currentBillingPeriod === "annual" ? t.profile.billingAnnual : t.profile.billingMonthly)
                .replace("{newPlan}", PLANS.find((p) => p.id === scheduledPlanId)?.name ?? scheduledPlanId?.toUpperCase() ?? "")
                .replace("{newPeriod}", scheduledBillingPeriod === "annual" ? t.profile.billingAnnual : t.profile.billingMonthly)}
            </DialogDescription>
          </DialogHeader>

          <div className="bg-amber-500/5 border border-amber-500/10 rounded-xl p-4">
            <p className="text-sm text-muted-foreground text-center">
              {t.pricing.cancelScheduledMessage
                .replace("{plan}", PLANS.find((p) => p.id === currentPlanId)?.name ?? currentPlanId?.toUpperCase() ?? "")
                .replace("{period}", currentBillingPeriod === "annual" ? t.profile.billingAnnual : t.profile.billingMonthly)
                .replace("{newPlan}", PLANS.find((p) => p.id === scheduledPlanId)?.name ?? scheduledPlanId?.toUpperCase() ?? "")
                .replace("{newPeriod}", scheduledBillingPeriod === "annual" ? t.profile.billingAnnual : t.profile.billingMonthly)}
            </p>
          </div>

          <div className="flex gap-3 mt-1">
            <Button
              variant="secondary"
              size="md"
              rounded="xl"
              onClick={handleCloseCancelScheduledDialog}
              className="flex-1"
            >
              {t.pricing.cancelScheduledDismiss}
            </Button>
            <Button
              variant="accent"
              size="md"
              rounded="xl"
              onClick={confirmCancelScheduledChange}
              disabled={isProcessing}
              className="flex-1"
            >
              {t.pricing.cancelScheduledConfirm}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Dialog (when downgrading to free) */}
      <CancelSubscriptionDialog
        open={showCancelDialog}
        onOpenChange={(open) => !open && handleCancelDialogClose()}
        onComplete={handleCancelComplete}
      />
    </>
  );
}
